import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Mail, Save, Send, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff } from 'lucide-react';

export default function EmailSettings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/email-settings');
      setS(r.data);
      if (!testTo && r.data.admin_notify_email) setTestTo(r.data.admin_notify_email);
    } catch { toast.error('Failed to load email settings.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const update = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      // Don't echo masked values back to the server.
      if (payload.resend_api_key && payload.resend_api_key.includes('\u2022')) delete payload.resend_api_key;
      if (payload.smtp_pass && payload.smtp_pass.includes('\u2022')) delete payload.smtp_pass;
      delete payload.resend_api_key_set; delete payload.smtp_pass_set;
      const r = await api.put('/admin/email-settings', payload);
      setS(r.data);
      toast.success('Email settings saved.');
    } catch (e) {
      toast.error('Save failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo || !testTo.includes('@')) { toast.error('Enter a valid test email first.'); return; }
    setTesting(true);
    try {
      const r = await api.post('/admin/email-settings/test', { to: testTo });
      toast.success(`Test email sent to ${r.data.delivered_to}`, { description: 'Check your inbox + spam folder.' });
    } catch (e) {
      toast.error('Test send failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally { setTesting(false); }
  };

  if (loading || !s) return <div className="py-10 text-center text-muted-kc" data-testid="email-loading"><Loader2 className="inline animate-spin mr-2" size={16}/> Loading…</div>;

  const provider = s.provider || 'disabled';
  const ready = (provider === 'resend' && s.resend_api_key_set) || (provider === 'smtp' && s.smtp_pass_set && s.smtp_host);

  return (
    <div data-testid="email-settings">
      <div className={`mb-5 p-4 rounded-[var(--kc-radius-md)] border ${ready ? 'border-[color:var(--kc-gold)] bg-gold-soft' : 'border-amber-200 bg-amber-50'}`}>
        <div className="flex items-center gap-2">
          {ready ? <CheckCircle2 size={16} className="text-[color:var(--kc-gold-2)]"/> : <AlertTriangle size={16} className="text-amber-600"/>}
          <div className="font-semibold text-sm">{ready ? `Email is LIVE via ${provider.toUpperCase()}.` : 'Email is DISABLED.'} </div>
        </div>
        <div className="text-xs text-ink-2 mt-1">{ready ? 'Password resets, waitlist + inquiry notifications will be delivered.' : 'Form notifications + password reset emails fall back to in-app inbox until you configure a provider below.'}</div>
      </div>

      <div className="space-y-4">
        <div className="p-5 bg-white border border-kc rounded-[var(--kc-radius-md)]">
          <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Provider</label>
          <div className="mt-2 flex gap-2 flex-wrap">
            {[
              { v: 'resend', label: 'Resend (recommended)' },
              { v: 'smtp',   label: 'Generic SMTP' },
              { v: 'disabled', label: 'Disabled' },
            ].map(o => (
              <button key={o.v} onClick={() => update('provider', o.v)} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold rounded-full transition-colors ${provider === o.v ? 'bg-ink text-white' : 'text-muted-kc hover:bg-surface-2 border border-kc'}`} data-testid={`email-provider-${o.v}`}>{o.label}</button>
            ))}
          </div>
        </div>

        {provider === 'resend' && (
          <div className="p-5 bg-white border border-kc rounded-[var(--kc-radius-md)] space-y-3">
            <div className="text-sm font-semibold inline-flex items-center gap-1.5"><Mail size={14}/> Resend setup</div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Resend API Key <span className="normal-case text-ink-2">(starts with <code>re_</code>)</span></label>
              <div className="relative mt-1">
                <input type={showKey ? 'text' : 'password'} value={s.resend_api_key || ''} onChange={(e) => update('resend_api_key', e.target.value)} placeholder="re_xxxxxxxxxxxxxxx" className="w-full h-10 px-3 pr-10 rounded-[var(--kc-radius-sm)] border border-kc bg-white font-mono text-sm focus:outline-none focus:border-[color:var(--kc-gold)]" data-testid="email-resend-key"/>
                <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-kc">{showKey ? <EyeOff size={14}/> : <Eye size={14}/>}</button>
              </div>
              <div className="text-xs text-muted-kc mt-1">{s.resend_api_key_set ? 'A key is currently set. To replace it, paste a new one.' : 'No key set yet — paste one from resend.com → API Keys.'}</div>
            </div>
            <PairField label="From address" hint="Use onboarding@resend.dev to start fast, or your verified domain (e.g. hello@kickasscourse.com)." value={s.sender_email} onChange={(v) => update('sender_email', v)} testid="email-sender" placeholder="onboarding@resend.dev"/>
            <PairField label="From name" hint="Friendly display name shown in inboxes." value={s.sender_name} onChange={(v) => update('sender_name', v)} testid="email-sender-name" placeholder="Kickass Course"/>
          </div>
        )}

        {provider === 'smtp' && (
          <div className="p-5 bg-white border border-kc rounded-[var(--kc-radius-md)] space-y-3">
            <div className="text-sm font-semibold inline-flex items-center gap-1.5"><Mail size={14}/> SMTP setup</div>
            <div className="grid grid-cols-2 gap-3">
              <PairField label="SMTP Host" value={s.smtp_host} onChange={(v) => update('smtp_host', v)} testid="email-smtp-host" placeholder="smtp.gmail.com"/>
              <PairField label="Port" value={s.smtp_port} onChange={(v) => update('smtp_port', v)} testid="email-smtp-port" placeholder="587"/>
              <PairField label="Username" value={s.smtp_user} onChange={(v) => update('smtp_user', v)} testid="email-smtp-user"/>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Password / app password</label>
                <input type="password" value={s.smtp_pass || ''} onChange={(e) => update('smtp_pass', e.target.value)} className="mt-1 w-full h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-sm" data-testid="email-smtp-pass"/>
                <div className="text-xs text-muted-kc mt-1">{s.smtp_pass_set ? 'Password is set.' : 'No password set.'}</div>
              </div>
            </div>
            <PairField label="From address" value={s.smtp_from} onChange={(v) => update('smtp_from', v)} testid="email-smtp-from"/>
            <PairField label="From name" value={s.smtp_from_name} onChange={(v) => update('smtp_from_name', v)} testid="email-smtp-fromname"/>
          </div>
        )}

        <div className="p-5 bg-white border border-kc rounded-[var(--kc-radius-md)]">
          <PairField label="Receiving inbox (for form notifications)" hint="Where waitlist signups, book-me forms, and contact inquiries get emailed." value={s.admin_notify_email} onChange={(v) => update('admin_notify_email', v)} testid="email-admin" placeholder="mickey@kickasslifecoach.com"/>
        </div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Send a test email to</label>
          <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="mt-1 w-full h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-sm" data-testid="email-test-to"/>
        </div>
        <button onClick={sendTest} disabled={testing || !ready} className="h-10 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border border-[color:var(--kc-gold)] bg-gold-soft text-ink font-semibold text-sm disabled:opacity-50" data-testid="email-test-send">{testing ? <><Loader2 size={14} className="animate-spin"/> Sending…</> : <><Send size={14}/> Send test</>}</button>
        <button onClick={save} disabled={saving} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold disabled:opacity-50" data-testid="email-save">{saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} Save settings</button>
      </div>

      <div className="mt-6 text-xs text-muted-kc space-y-1">
        <p>• Reset a sensitive field by clearing it manually (paste empty), saving, then setting a new value.</p>
        <p>• Free Resend accounts can only deliver to verified recipient addresses until you verify a sending domain.</p>
        <p>• Changes take effect immediately on the running server — no redeploy needed.</p>
      </div>
    </div>
  );
}

function PairField({ label, hint, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">{label}</label>
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || ''} className="mt-1 w-full h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-sm focus:outline-none focus:border-[color:var(--kc-gold)]" data-testid={testid}/>
      {hint && <div className="text-xs text-muted-kc mt-1">{hint}</div>}
    </div>
  );
}
